import { useEffect, useRef, useState } from "react";
import { Bell, History } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { timeAgo } from "../../lib/format";
import {
  notificationTypeLabel,
  resolveNotificationPath,
  type NotificationItem,
} from "../../lib/notifications";
import { useShellTabs } from "../../stores/shell-tabs";

const PREVIEW_LIMIT = 5;

export function NotificationBell() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const openInActiveTab = useShellTabs((s) => s.openInActiveTab);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const { data } = useQuery({
    queryKey: ["notifications", "preview"],
    queryFn: () =>
      api<{ items: NotificationItem[]; unread: number }>(`/notifications?limit=${PREVIEW_LIMIT}`),
    staleTime: 30_000,
  });

  const markReadMut = useMutation({
    mutationFn: (ids?: string[]) => api("/notifications/read", { method: "POST", body: ids ? { ids } : {} }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current?.contains(event.target as Node)) return;
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("mousedown", onPointerDown);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const unread = data?.unread ?? 0;
  const items = data?.items ?? [];

  function openNotification(item: NotificationItem) {
    if (!item.readAt) {
      markReadMut.mutate([item.id]);
    }
    const path = resolveNotificationPath(item);
    setOpen(false);
    if (path) {
      const tabPath = openInActiveTab(path);
      navigate(tabPath);
    }
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="relative rounded-xl p-2 text-muted transition-colors hover:bg-hover hover:text-foreground"
        aria-label="Notifications"
        aria-expanded={open}
        aria-haspopup="true"
      >
        <Bell size={18} />
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand-pink px-1 text-[9px] font-bold text-white">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-[calc(100%+6px)] z-[10040] w-[min(22rem,calc(100vw-1.5rem))] overflow-hidden rounded-xl border border-line bg-surface shadow-xl">
          <div className="flex items-center justify-between gap-2 border-b border-line px-3 py-2.5">
            <p className="text-xs font-medium text-foreground">
              Notifications{unread > 0 ? ` (${unread} unread)` : ""}
            </p>
            {unread > 0 && (
              <button
                type="button"
                className="shrink-0 text-[11px] font-medium text-brand-pink hover:brightness-110"
                onClick={() => markReadMut.mutate(undefined)}
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-[min(20rem,50vh)] overflow-y-auto">
            {items.length ? (
              items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`flex w-full gap-2.5 border-b border-line/60 px-3 py-2.5 text-left transition-colors last:border-b-0 hover:bg-hover ${
                    item.readAt ? "opacity-70" : "bg-brand-pink/[0.06]"
                  }`}
                  onClick={() => openNotification(item)}
                >
                  <History size={14} className="mt-0.5 shrink-0 text-muted-2" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-foreground">{item.title}</p>
                    <p className="mt-0.5 truncate text-[10px] capitalize text-muted">
                      {item.body ?? notificationTypeLabel(item.type)}
                    </p>
                    <p className="mt-1 text-[10px] text-muted-2">{timeAgo(item.createdAt)}</p>
                  </div>
                </button>
              ))
            ) : (
              <p className="px-3 py-8 text-center text-xs text-muted-2">Nothing here yet</p>
            )}
          </div>

          <div className="border-t border-line px-3 py-2 text-center">
            <Link
              to="/notifications"
              className="text-xs font-medium text-brand-pink hover:brightness-110"
              onClick={() => setOpen(false)}
            >
              View all
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
